import { ReactNode, forwardRef, useImperativeHandle, useRef, useMemo } from 'react'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { TGetItemFn, TPlan, TSelectPlan, TSetItemFn } from '@/config/types'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'

import { SelectPlan, SelectPlanV2 } from '@/components/plans/selectPlan'

import { TPaymentScreenPageOptions } from '@/commonPages/paymentScreenPage/types'

import { TSelectPlanBlockControl } from './types'
import { WalletOrPrimaryButton } from './WalletOrPrimaryButton'

const paypalOptions = getDefaultPayPalOptions()

export type TSelectPlanBlockWithApplePayProps = {
  pageConfig: TSelectPlan
  plans: TPlan[]
  selectedPlan: TPlan
  selectPlan: (plan: TPlan) => void
  scrollToAnchor: () => void
  onSelect: () => void
  getItem: TGetItemFn
  setItem: TSetItemFn
  walletButtonTheme: StripeWalletButtonTheme
  className?: string
  buttonName?: string
  submitButtonClassName?: string
  additionalBlock?: () => ReactNode
  specificPlanDetailsTitle?: string
  containerWrapperClassName?: string
  paymentOptions: TPaymentScreenPageOptions
}

export const SelectPlanBlockWithApplePay = forwardRef<
  TSelectPlanBlockControl,
  TSelectPlanBlockWithApplePayProps
>(function SelectPlanBlock(
  {
    pageConfig,
    plans,
    selectedPlan,
    selectPlan,
    scrollToAnchor,
    onSelect,
    getItem,
    setItem,
    walletButtonTheme,
    submitButtonClassName,
    additionalBlock,
    specificPlanDetailsTitle,
    containerWrapperClassName,
    paymentOptions,
  }: TSelectPlanBlockWithApplePayProps,
  ref
) {
  const {
    planDetailsTitle,
    buttonName,
    afterSelectPlanBlocks,
    afterButtonBlocks,
    smartphoneInfo,
    isShowInput,
    concatPrice,
    showSelectPlanBlockV2 = false,
  } = pageConfig

  const inputRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(
    ref,
    () => {
      return {
        scrollIntoView() {
          inputRef.current?.scrollIntoView()
        },
      }
    },
    []
  )

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(selectedPlan, paymentOptions.stripeFormAppearance),
    [selectedPlan, paymentOptions.stripeFormAppearance]
  )

  const PlanComponent = showSelectPlanBlockV2 ? SelectPlanV2 : SelectPlan

  let containerClassName = 'select-plan-block-wrapper with-apple-pay '
  if (containerWrapperClassName) {
    containerClassName += containerWrapperClassName
  }

  return (
    <PayPalScriptProvider options={{ ...paypalOptions }}>
      <Elements stripe={paymentOptions.stripePromise} options={stripeOptions}>
        <div className={containerClassName}>
          <div className={showSelectPlanBlockV2 ? 'select-plan-block-v2' : 'select-plan-block'}>
            <PlanComponent
              ref={inputRef}
              planTitle={specificPlanDetailsTitle || planDetailsTitle}
              planDetails={plans}
              selectPlan={selectPlan}
              selectedPlan={selectedPlan}
              isShadow
              additionalBlock={additionalBlock && additionalBlock()}
              isShowInput={isShowInput}
              concatPrice={concatPrice}
            />
            {/* TODO: Rename afterSelectPlanBlocks to beforeButtonBlocks */}
            {afterSelectPlanBlocks &&
              afterSelectPlanBlocks.map((component) => {
                return component(selectedPlan, scrollToAnchor)
              })}
            <div className="button-container prime">
              <WalletOrPrimaryButton
                selectedPlan={selectedPlan}
                getItem={getItem}
                setItem={setItem}
                onClick={onSelect}
                walletButtonTheme={walletButtonTheme}
                paymentOptions={paymentOptions}
                buttonName={buttonName}
                buttonClassName={submitButtonClassName}
              />
              <div className="smartphone-info">{smartphoneInfo}</div>
            </div>
            {afterButtonBlocks &&
              afterButtonBlocks.map((component) => {
                return component(selectedPlan, scrollToAnchor)
              })}
          </div>
        </div>
      </Elements>
    </PayPalScriptProvider>
  )
})
